import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  Search,
  Settings2,
  ChevronRight,
  CreditCard,
  TrendingUp,
  Percent,
  Wallet,
  Building2,
  PieChart // Icon for commission
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Gestión de Métodos de Cobro (Estilo Productos)
 * Refactorizado: Diseño Nova + Columna Comisión
 */
function MetodosCobro() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [metodos, setMetodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (profile?.organization_id) fetchMetodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const fetchMetodos = async () => {
    setLoading(true);
    try {
      // Hacemos join con la tabla banks para mostrar el nombre del banco
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*, banks(name)')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setMetodos(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar métodos de cobro');
    } finally {
      setLoading(false);
    }
  };

  // KPIs calculados
  const kpis = useMemo(() => {
    const total = metodos.length;
    const conRecargo = metodos.filter((m) => (m.surcharge_percentage || 0) > 0).length;
    const conDescuento = metodos.filter((m) => (m.discount_percentage || 0) > 0).length;
    // Nuevo KPI opcional: promedio de comisiones
    // const avgComision = metodos.reduce((acc, m) => acc + (m.commission_percentage || 0), 0) / (total || 1);

    return { total, conRecargo, conDescuento };
  }, [metodos]);

  // Filtrado
  const filteredMetodos = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return metodos.filter((m) =>
      m.name.toLowerCase().includes(term) ||
      (m.banks?.name || '').toLowerCase().includes(term)
    );
  }, [metodos, searchTerm]);

  return (
    // NUEVA ESTRUCTURA NOVA
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <CreditCard size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Métodos de Cobro</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Pasarelas, bancos y efectivo.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <button
            data-onboarding-id="payment-method-new-btn"
            onClick={() => navigate('/sistema/metodos-cobro-form')}
            className="px-5 py-2.5 rounded-2xl bg-[#3ff2aa] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 border border-[#3ff2aa]"
          >
            <Plus size={16} strokeWidth={3} /> Nuevo Método
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Métodos</p>
            <Wallet size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Activos</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Con Recargo</p>
            <TrendingUp size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.conRecargo}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Aumentan precio</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Con Descuento</p>
            <Percent size={16} className="text-rose-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.conDescuento}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Bonificados</p>
        </div>
      </div>

      {/* BUSCADOR */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4">
         <div className="relative">
             <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                <Search size={10} /> Buscar Método
             </label>
             <div className="relative">
                <input 
                    type="text"
                    placeholder="NOMBRE O BANCO..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase text-slate-700"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
             </div>
         </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
        {/* Header Tabla */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Configuración de Cobros
                </span>
            </div>
            <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">
                {filteredMetodos.length} Opciones
            </span>
        </div>

        <div data-onboarding-id="payment-methods-list" className="overflow-x-auto imp-table-wrap">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-5 py-3 pl-5">Método de Pago</th>
                <th className="px-4 py-3 text-right">Comisión (Costo)</th>
                <th className="px-4 py-3 text-right">Acreditacion</th>
                <th className="px-4 py-3 text-right">Recargo (Cliente)</th>
                <th className="px-4 py-3 text-right">Descuento (Cliente)</th>
                <th className="px-4 py-3 text-right pr-5">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Cargando métodos...
                  </td>
                </tr>
              ) : (
                filteredMetodos.map((m) => (
                  <tr 
                    key={m.id} 
                    className="hover:bg-blue-50/20 transition-all cursor-pointer group"
                    onClick={() => navigate(`/sistema/metodos-cobro-form/${m.id}`)}
                  >
                    <td className="px-5 py-3 pl-5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm shrink-0">
                          <Wallet size={14} />
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-slate-700 uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors">
                            {m.name}
                          </p>
                          <div className="flex items-center gap-1 text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                             <Building2 size={10} /> {m.banks?.name || 'Caja Central'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                       {/* COLUMNA COMISIÓN AGREGADA */}
                       <div className="flex flex-col items-end">
                          <span className={`text-[10px] font-black ${m.commission_percentage > 0 ? 'text-rose-500' : 'text-slate-300'}`}>
                             {m.commission_percentage > 0 ? `${m.commission_percentage}%` : '0%'}
                          </span>
                          <span className="text-[8px] font-bold text-slate-400 uppercase">Costo Op.</span>
                       </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] font-black text-slate-600">
                          {Number(m.accreditation_days || 0)} dia{Number(m.accreditation_days || 0) === 1 ? '' : 's'}
                        </span>
                        <span className="text-[8px] font-bold text-slate-400 uppercase">
                          {m.accreditation_business_days ? 'Habiles' : 'Corridos'}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      {m.surcharge_percentage > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded bg-emerald-50 border border-emerald-100 text-emerald-600 text-[9px] font-black">
                          +{m.surcharge_percentage}%
                        </span>
                      ) : (
                        <span className="text-slate-300 text-[10px]">-</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      {m.discount_percentage > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded bg-rose-50 border border-rose-100 text-rose-600 text-[9px] font-black">
                          -{m.discount_percentage}%
                        </span>
                      ) : (
                        <span className="text-slate-300 text-[10px]">-</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right pr-5">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/sistema/metodos-cobro-form/${m.id}`); }}
                          className="p-1.5 text-slate-300 hover:text-[#51A7FD] hover:bg-blue-50 rounded-lg transition-all"
                          title="Configurar"
                        >
                          <Settings2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {filteredMetodos.length === 0 && !loading && (
                 <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                       <CreditCard className="mx-auto text-slate-200 mb-2" size={32} />
                       <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No se encontraron métodos</p>
                    </td>
                 </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default MetodosCobro;
